// src/pages/LoginPage.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom'; 
import { supabase, BACKEND_URL } from '../supabaseClient'; 
import { Toaster, toast } from 'react-hot-toast';
import { Turnstile } from '@marsidev/react-turnstile'; 
import './LoginPage.css'; 

function LoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authMode, setAuthMode] = useState('signIn'); 
  const [turnstileToken, setTurnstileToken] = useState(null);

  const handleGoogleLogin = async () => {
    if (!turnstileToken) {
        toast.error("Please complete the security check first.");
        return;
    }

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
      });
      if (error) throw error;
    } catch (error) {
      toast.error(error.error_description || error.message);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!turnstileToken) {
      toast.error("Please complete the security check.");
      return;
    }

    setLoading(true);
    
    try {
      if (authMode === 'signIn') {
        // SECURE LOGIN FLOW
        const response = await fetch(`${BACKEND_URL}/api/auth/login-secure`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, turnstileToken })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Login failed");
        }

        const { error: sessionError } = await supabase.auth.setSession(data.session);
        if (sessionError) throw sessionError;

        navigate('/app/apparel');

      } else {
        // SIGN UP FLOW
        const { error } = await supabase.auth.signUp({
          email: email,
          password: password,
        });
        if (error) throw error;
        
        toast.success("Account created! Please check your email to verify.", { duration: 6000 });
        setEmail('');
        setPassword('');
        setAuthMode('signIn');
      }
    } catch (error) {
      console.error("Auth Error:", error);
      toast.error(error.message || "Authentication failed");
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="login-box">
        {/* REMOVED LOGO SECTION HERE
            The header already contains the logo.
        */}
        
        {/* Google Button */}
        <button 
            className="login-button google-button" 
            onClick={handleGoogleLogin} 
            disabled={loading || !turnstileToken}
            style={{ opacity: !turnstileToken ? 0.6 : 1, cursor: !turnstileToken ? 'not-allowed' : 'pointer' }}
        >
          <img 
            src="/images/showcase/Google_Favicon_2025.svg" 
            alt="Google" 
            className="google-logo" 
          />
          Sign in with Google
        </button>

        <p className="divider-text"><span>or</span></p>

        <form onSubmit={handleEmailAuth}>
          <div className="input-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="input-group">
            <div className="label-row">
              <label htmlFor="password">Password</label>
              {authMode === 'signIn' && (
                <Link to="/forgot-password" class="forgot-password-link">
                  Forgot password?
                </Link>
              )}
            </div>
            <input
              id="password"
              type="password"
              placeholder="Your Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="captcha-wrapper">
             <Turnstile 
                siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY} 
                onSuccess={(token) => setTurnstileToken(token)}
                onError={() => setTurnstileToken(null)}
                onExpire={() => setTurnstileToken(null)}
                options={{ theme: 'auto', size: 'normal' }}
             />
          </div>

          <button 
            className="login-button email-button" 
            type="submit" 
            disabled={loading || !turnstileToken}
          >
            {loading ? (
              <div className="spinner-small"></div>
            ) : (
              authMode === 'signIn' ? 'Sign In' : 'Sign Up'
            )}
          </button>
        </form>

        <div className="auth-toggle">
          {authMode === 'signIn' ? (
            <p>
              Need an account?{' '}
              <span onClick={() => !loading && setAuthMode('signUp')}>Sign Up</span>
            </p>
          ) : (
            <p>
              Already have an account?{' '}
              <span onClick={() => !loading && setAuthMode('signIn')}>Sign In</span>
            </p>
          )}
        </div>

      </div>
    </div>
  );
}

export default LoginPage;